import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Button, Card, EmptyState, IconButton, Menu, PageHeader, Pill, Skeleton, useConfirm, useToast } from '../ui';
import { CheckIcon, CloseIcon, MailIcon, MoreIcon, ShieldIcon, UserIcon, UsersIcon } from '../ui/icons';
import api from '../config/api';
import { errorMessage } from '../errors';
import { notifyChanged, useOnChanged } from '../shell/changes';
import { sectionCrumb } from '../settings/SectionLayout';
import { initials } from '../shell/collections';

const STATUSES = ['pending', 'active', 'disabled', 'rejected'];
const STATUS_TONES = { pending: 'amber', active: 'green', disabled: 'neutral', rejected: 'danger' };
const ROLES = ['admin', 'user'];
// Same tones as the Invite dialog's list.
const INVITATION_TONES = { sent: 'accent', accepted: 'green', revoked: 'neutral', expired: 'amber' };
const invitationStatus = (inv) => (inv.status === 'sent' && inv.expired ? 'expired' : inv.status);

/**
 * Every invitation, whoever sent it: those still waiting for an answer, then
 * the earlier ones (accepted, revoked, expired) behind a toggle. An invitee
 * becomes an account only once they accept, so this is where they show until then.
 */
function InvitationsSection() {
  const { t } = useTranslation();
  const confirm = useConfirm();
  const toast = useToast();
  const [invitations, setInvitations] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(null);
  const [showEarlier, setShowEarlier] = useState(false);

  const load = useCallback(
    () => api.get('/invitations', { params: { scope: 'all' } })
      .then((r) => { setInvitations(r.data); setError(''); })
      .catch(() => { setInvitations((list) => list || []); setError(t('accounts.invitations.loadError')); }),
    [t]
  );
  useEffect(() => { load(); }, [load]);
  useOnChanged('invitations', load);

  const revoke = async (inv) => {
    const ok = await confirm({
      title: t('accounts.invitations.confirmRevoke', { email: inv.email }),
      confirmLabel: t('accounts.invitations.revoke'),
      cancelLabel: t('accounts.invitations.keep'),
      danger: true,
    });
    if (!ok) return;
    setError('');
    setBusy(inv.id);
    try {
      await api.delete(`/invitations/${inv.id}`);
      toast.success(t('accounts.invitations.revoked'));
    } catch (err) {
      setError(errorMessage(t, err, 'invite.revokeError'));
    } finally {
      setBusy(null);
    }
    load();
  };

  const list = invitations || [];
  const waiting = list.filter((inv) => invitationStatus(inv) === 'sent');
  const earlier = list.filter((inv) => invitationStatus(inv) !== 'sent');
  const shown = showEarlier ? [...waiting, ...earlier] : waiting;

  const table = (
    <div className="data-table-wrap">
      <table role="table" className="data-table data-table--stack accounts__table">
        <thead>
          <tr role="row">
            <th role="columnheader" scope="col">{t('accounts.invitations.col.email')}</th>
            <th role="columnheader" scope="col">{t('accounts.col.status')}</th>
            <th role="columnheader" scope="col">{t('accounts.invitations.col.invitedBy')}</th>
            <th role="columnheader" scope="col">{t('accounts.invitations.col.sent')}</th>
            <th role="columnheader" scope="col">{t('accounts.invitations.col.expires')}</th>
            <th role="columnheader" scope="col"><span className="visually-hidden">{t('accounts.col.actions')}</span></th>
          </tr>
        </thead>
        <tbody>
          {shown.map((inv) => {
            const status = invitationStatus(inv);
            return (
              <tr role="row" key={inv.id}>
                <th role="rowheader" scope="row"><span className="accounts__name">{inv.email}</span></th>
                <td role="cell" data-label={t('accounts.col.status')}>
                  <Pill tone={INVITATION_TONES[status] || 'neutral'}>{t(`invite.status.${status}`, { defaultValue: inv.status })}</Pill>
                </td>
                <td role="cell" data-label={t('accounts.invitations.col.invitedBy')}>{inv.invited_by_name || '—'}</td>
                <td role="cell" data-label={t('accounts.invitations.col.sent')} className="accounts__date">{inv.created_at ? inv.created_at.slice(0, 16) : '—'}</td>
                <td role="cell" data-label={t('accounts.invitations.col.expires')} className="accounts__date">{inv.expires_at ? inv.expires_at.slice(0, 16) : '—'}</td>
                <td role="cell" className="data-table__actions">
                  {status === 'sent' && (
                    <div className="accounts__actions">
                      <Button size="sm" variant="secondary" icon={CloseIcon} className="accounts__danger" onClick={() => revoke(inv)}
                              disabled={busy === inv.id} aria-label={t('accounts.invitations.revokeNamed', { email: inv.email })}>
                        {t('accounts.invitations.revoke')}
                      </Button>
                    </div>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );

  return (
    <section aria-labelledby="accounts-invitations">
      <h2 id="accounts-invitations" className="accounts__heading">{t('accounts.invitations.title')}</h2>
      {error && <Alert tone="danger">{error}</Alert>}
      {invitations === null ? <Skeleton height={80} radius="var(--radius-lg)" />
        : list.length === 0 ? <p className="settings-muted">{t('accounts.invitations.none')}</p>
          : (
            <>
              {waiting.length === 0 && !showEarlier && <p className="settings-muted">{t('accounts.invitations.noneWaiting')}</p>}
              {shown.length > 0 && table}
              {earlier.length > 0 && (
                <Button variant="ghost" size="sm" className="accounts__more" aria-expanded={showEarlier} onClick={() => setShowEarlier((v) => !v)}>
                  {showEarlier ? t('accounts.invitations.hideEarlier') : t('accounts.invitations.showEarlier', { count: earlier.length })}
                </Button>
              )}
            </>
          )}
    </section>
  );
}

/**
 * /admin/accounts — requests waiting for approval (with their message),
 * then every account: approve, reject, make administrator or member,
 * disable, enable. Questions come first for the actions that need them.
 */
function AccountsAdmin({ currentUserId, onInvite }) {
  const { t } = useTranslation();
  const confirm = useConfirm();
  const toast = useToast();
  const [users, setUsers] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(null);   // the id of the account being changed

  const load = useCallback(
    () => api.get('/admin/users').then((r) => setUsers(r.data)).catch(() => { setUsers((list) => list || []); setError('load'); }),
    []
  );
  useEffect(() => { load(); }, [load]);

  // question: {title, confirmLabel, danger} asked first, when the action needs it.
  const act = async (user, request, question) => {
    if (question && !(await confirm(question))) return;
    setError('');
    setBusy(user.id);
    try {
      await request();
      await load();
      notifyChanged('accounts');
      toast.success(t('accounts.done'));
    } catch (err) {
      setError(errorMessage(t, err, 'common.actionFailed'));
    } finally {
      setBusy(null);
    }
  };

  const approve = (u) => act(u, () => api.post(`/admin/users/${u.id}/approve`));
  const reject = (u) => act(u, () => api.post(`/admin/users/${u.id}/reject`),
    { title: t('accounts.confirmReject', { email: u.email }), confirmLabel: t('accounts.reject'), danger: true });

  const status = (u) => (
    <Pill tone={STATUS_TONES[u.status] || 'neutral'}>{STATUSES.includes(u.status) ? t(`accounts.status.${u.status}`) : u.status}</Pill>
  );

  const actions = (u) => {
    // Ids may arrive as strings depending on the PDO driver; compare as numbers.
    const isMe = Number(u.id) === Number(currentUserId);
    const loading = busy === u.id;
    return (
      <div className="accounts__actions">
        {u.status === 'pending' && (
          <>
            <Button size="sm" icon={CheckIcon} onClick={() => approve(u)} disabled={loading}
                    aria-label={t('accounts.approveNamed', { email: u.email })}>{t('accounts.approve')}</Button>
            <Button size="sm" variant="secondary" icon={CloseIcon} className="accounts__danger" onClick={() => reject(u)} disabled={loading}
                    aria-label={t('accounts.rejectNamed', { email: u.email })}>{t('accounts.reject')}</Button>
          </>
        )}
        {!isMe && (u.status === 'active' || u.status === 'disabled') && (
          <Menu
            label={t('accounts.actionsFor', { email: u.email })}
            align="end"
            items={u.status === 'active' ? [
              {
                id: 'role',
                label: u.role === 'admin' ? t('accounts.makeUser') : t('accounts.makeAdmin'),
                icon: u.role === 'admin' ? UserIcon : ShieldIcon,
                onSelect: () => act(u, () => api.put(`/admin/users/${u.id}/role`, { role: u.role === 'admin' ? 'user' : 'admin' }),
                  u.role === 'admin'
                    ? { title: t('accounts.confirmRemoveAdmin', { email: u.email }), confirmLabel: t('accounts.makeUser') }
                    : { title: t('accounts.confirmMakeAdmin', { email: u.email }), confirmLabel: t('accounts.makeAdmin') }),
              },
              'separator',
              {
                id: 'disable',
                label: t('accounts.disable'),
                icon: CloseIcon,
                danger: true,
                onSelect: () => act(u, () => api.post(`/admin/users/${u.id}/disable`),
                  { title: t('accounts.confirmDisable', { email: u.email }), confirmLabel: t('accounts.disable'), danger: true }),
              },
            ] : [
              { id: 'enable', label: t('accounts.enable'), icon: CheckIcon, onSelect: () => act(u, () => api.post(`/admin/users/${u.id}/enable`)) },
            ]}
            trigger={(props) => (
              <IconButton {...props} icon={MoreIcon} variant="ghost" disabled={loading} label={t('accounts.actionsFor', { email: u.email })} />
            )}
          />
        )}
      </div>
    );
  };

  const who = (u) => (
    <span className="accounts__who">
      <span className="shell-account__avatar" aria-hidden="true">{initials(u.display_name || u.email)}</span>
      <span className="accounts__names">
        <span className="accounts__name">{u.display_name}</span>
        <span className="accounts__email">{u.email}</span>
      </span>
    </span>
  );

  const header = (
    <PageHeader
      breadcrumb={[sectionCrumb(t, 'admin'), { label: t('accounts.title') }]}
      title={t('accounts.title')}
      actions={onInvite && <Button variant="secondary" icon={MailIcon} onClick={onInvite}>{t('nav.invite')}</Button>}
    />
  );

  if (users === null) {
    return <>{header}<Skeleton height={240} radius="var(--radius-lg)" /></>;
  }

  const pending = users.filter((u) => u.status === 'pending');
  const others = users.filter((u) => u.status !== 'pending');

  return (
    <div className="accounts">
      {header}
      <div className="settings-stack">
        {error && <Alert tone="danger">{error === 'load' ? t('accounts.loadError') : error}</Alert>}

        <section aria-labelledby="accounts-waiting">
          <h2 id="accounts-waiting" className="accounts__heading">{t('accounts.waiting', { count: pending.length })}</h2>
          {pending.length ? (
            <ul className="accounts__requests">
              {pending.map((u) => (
                <li key={u.id}>
                  <Card className="accounts__request">
                    {who(u)}
                    {u.invited_by_name && <p className="settings-muted">{t('accounts.invitedBy', { name: u.invited_by_name })}</p>}
                    {u.request_message && <blockquote className="accounts__message">{u.request_message}</blockquote>}
                    {actions(u)}
                  </Card>
                </li>
              ))}
            </ul>
          ) : <p className="settings-muted">{t('accounts.nobodyWaiting')}</p>}
        </section>

        <InvitationsSection />

        <section aria-labelledby="accounts-all">
          <h2 id="accounts-all" className="accounts__heading">{t('accounts.all')}</h2>
          {others.length === 0 ? (
            <EmptyState icon={UsersIcon} title={t('accounts.none')} />
          ) : (
            <div className="data-table-wrap">
              <table role="table" className="data-table data-table--stack accounts__table">
                <thead>
                  <tr role="row">
                    <th role="columnheader" scope="col">{t('accounts.col.account')}</th>
                    <th role="columnheader" scope="col">{t('accounts.col.status')}</th>
                    <th role="columnheader" scope="col">{t('accounts.col.role')}</th>
                    <th role="columnheader" scope="col">{t('accounts.col.inventories')}</th>
                    <th role="columnheader" scope="col">{t('accounts.col.lastLogin')}</th>
                    <th role="columnheader" scope="col"><span className="visually-hidden">{t('accounts.col.actions')}</span></th>
                  </tr>
                </thead>
                <tbody>
                  {others.map((u) => (
                    <tr role="row" key={u.id}>
                      <th role="rowheader" scope="row">{who(u)}</th>
                      <td role="cell" data-label={t('accounts.col.status')}>
                        {status(u)}
                        {u.invited_by_name && <span className="accounts__note">{t('accounts.invitedBy', { name: u.invited_by_name })}</span>}
                        {u.status === 'active' && !Number(u.has_password) && <span className="accounts__note">{t('accounts.noPasswordYet')}</span>}
                      </td>
                      <td role="cell" data-label={t('accounts.col.role')}>{ROLES.includes(u.role) ? t(`accounts.role.${u.role}`) : u.role}</td>
                      <td role="cell" data-label={t('accounts.col.inventories')} className="accounts__number">{u.inventory_count}</td>
                      <td role="cell" data-label={t('accounts.col.lastLogin')} className="accounts__date">{u.last_login_at ? u.last_login_at.slice(0, 16) : '—'}</td>
                      <td role="cell" className="data-table__actions">{actions(u)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

export default AccountsAdmin;
